import { GeoJSONSource, type Map as MlMap, type ExpressionSpecification } from 'maplibre-gl'
import type { GeoPayload } from '../../lib/types'
import { fenceColorExpr, fenceIconExpr, registerFenceIcons } from './fenceIcons'
import { LYR_CLUSTER } from './vehicles'

/**
 * The Google Earth project, rendered: 25 geofences and the two route polylines.
 *
 * Reference geometry, not signal. The polygons stay quiet -- low fill, hairline edge --
 * because the moment a polygon competes with a vehicle marker the map has stopped being an
 * ops tool. What carries the information is the BADGE at each fence centroid: a shaped,
 * glyphed marker that says toll, ghat, bridge, gate, yard at a glance. See fenceIcons.ts
 * for why shape rather than colour does the work.
 *
 * Zoom behaviour is the whole design here, and it mirrors what the fleet markers do:
 *
 *   z < 9      nothing but the corridor line. At 173 km on screen, 25 badges is confetti.
 *   z 9 - 11   badges only, no names. Enough to read the shape of the corridor.
 *   z >= 11    badges plus names, because at that scale they no longer collide.
 *
 * Laden and empty routes are distinguished by dash pattern rather than colour. Colour on
 * this console means signal state and nothing else -- worth more than the two seconds it
 * costs to learn the dash. Direction, where it is shown, uses the reference's own pair:
 * sky southbound to Dhule, violet northbound to Dhar.
 */

export const SRC_FENCES = 'fences'
export const SRC_ROUTES = 'routes'
export const LYR_FENCE_FILL = 'fence-fill'
export const LYR_FENCE_LINE = 'fence-line'
export const LYR_FENCE_BADGE = 'fence-badge'
export const LYR_FENCE_LABEL = 'fence-label'
export const LYR_ROUTE_CASING = 'route-casing'
export const LYR_ROUTE_LINE = 'route-line'
export const LYR_ROUTE_LINE_EMPTY = 'route-line-empty'

// NO CORRIDOR BAND. There used to be a 2 km-wide translucent blue line here standing in
// for the diversion tolerance. It read fine at corridor zoom and became a wash of colour
// over the whole viewport the moment anyone zoomed in to look at a truck -- 2 km on the
// ground is the entire screen at z15. The tolerance is a number in the diversion rule,
// not something the map has to paint, and off-route vehicles already announce themselves
// through DIVERSION alerts. Removed rather than zoom-clamped: a band that vanishes as you
// approach the thing it describes was never earning its pixels.

/**
 * The three route-line layer specs (casing + laden + empty), as plain objects -- pulled
 * out of addReferenceLayers so a test can validate them against MapLibre's own style
 * validator without needing a real map/canvas (registerFenceIcons, called by
 * addReferenceLayers, needs a real 2D canvas context jsdom doesn't provide).
 *
 * TWO route-line layers, not one with a data-driven dasharray. The corridor line was
 * reported missing entirely on production (fences, badges, basemap, vehicles all fine --
 * only the route polyline itself never appeared, no console error). The one genuinely odd
 * thing in the single-layer version this replaced: 'line-dasharray': ['case', <per-feature
 * ['get','route_id'] check>, ['literal', [2, 2]], ['literal', [1]]] -- an ODD-LENGTH
 * fallback array ([1], not a real dash/gap pair) chosen via a data expression. Neither
 * MapLibre's static style-spec validator nor the installed maplibre-gl version's own
 * documented support window flags this as invalid, so this is the SUSPECTED cause from
 * static analysis, not a confirmed one -- there was no way to reproduce it in a live
 * browser (no console login available). Regardless of the exact trigger, this is strictly
 * safer either way: two layers, filtered by route_id instead of branching inside paint,
 * each with an unambiguous static (well-formed, even-length or absent) dasharray -- the
 * idiomatic MapLibre pattern for varying a property per feature that isn't itself meant to
 * be data-driven.
 */
export function routeLineLayerSpecs(): [
  { id: string; type: 'line'; source: string; layout: Record<string, unknown>; paint: Record<string, unknown> },
  { id: string; type: 'line'; source: string; filter: ExpressionSpecification; layout: Record<string, unknown>; paint: Record<string, unknown> },
  { id: string; type: 'line'; source: string; filter: ExpressionSpecification; layout: Record<string, unknown>; paint: Record<string, unknown> },
] {
  const laden: ExpressionSpecification =
    ['!', ['in', 'empty', ['coalesce', ['get', 'route_id'], '']]] as unknown as ExpressionSpecification
  const empty: ExpressionSpecification =
    ['in', 'empty', ['coalesce', ['get', 'route_id'], '']] as unknown as ExpressionSpecification
  const layout = { 'line-cap': 'round', 'line-join': 'round' }
  return [
    {
      // A casing under the route line. One pale stroke behind a darker one is what stops
      // a polyline from dissolving into a busy basemap; it costs a layer and buys
      // legibility at every zoom, which is the trade a road atlas has been making for a
      // century.
      id: LYR_ROUTE_CASING, type: 'line', source: SRC_ROUTES, layout,
      paint: {
        'line-color': '#FFFFFF', 'line-opacity': 0.85,
        'line-width': ['interpolate', ['linear'], ['zoom'], 6, 3.4, 12, 6.5, 16, 10],
      },
    },
    {
      id: LYR_ROUTE_LINE, type: 'line', source: SRC_ROUTES, filter: laden, layout,
      paint: {
        'line-color': '#0F172A', 'line-opacity': 0.72,
        'line-width': ['interpolate', ['linear'], ['zoom'], 6, 1.2, 12, 2.6, 16, 4],
      },
    },
    {
      id: LYR_ROUTE_LINE_EMPTY, type: 'line', source: SRC_ROUTES, filter: empty, layout,
      paint: {
        'line-color': '#0F172A', 'line-opacity': 0.72,
        'line-width': ['interpolate', ['linear'], ['zoom'], 6, 1.2, 12, 2.6, 16, 4],
        'line-dasharray': [2, 2],
      },
    },
  ]
}

export function addReferenceLayers(map: MlMap, geo: GeoPayload): void {
  registerFenceIcons(map)

  if (!map.getSource(SRC_ROUTES)) {
    map.addSource(SRC_ROUTES, { type: 'geojson', data: geo.routes, promoteId: 'route_id' })
  }
  if (!map.getSource(SRC_FENCES)) {
    map.addSource(SRC_FENCES, { type: 'geojson', data: geo.geofences, promoteId: 'geofence_id' })
  }

  // Reference geometry must stay UNDER the trucks, not just happen to -- whichever of
  // addReferenceLayers/addVehicleLayers this map's own useEffect calls second otherwise
  // wins the stacking order by accident. Confirmed live: geofences/routes often arrive
  // from the API after the map's style.load has already run addVehicleLayers once (see
  // MapCanvas.tsx's reactive "reference geometry" effect, which re-calls this function
  // whenever that data shows up), so without an explicit beforeId the route line and
  // fence polygons would get added ON TOP of trucks already on screen -- same bug
  // MbmtLiveMap.tsx's own road-overlay comment already names for a different map.
  // undefined (not yet added) is fine: addVehicleLayers runs after this in the normal
  // style.load order, so the route still ends up underneath either way.
  const beforeId = map.getLayer(LYR_CLUSTER) ? LYR_CLUSTER : undefined

  for (const spec of routeLineLayerSpecs()) {
    if (!map.getLayer(spec.id)) map.addLayer(spec as never, beforeId)
  }

  if (!map.getLayer(LYR_FENCE_FILL)) {
    map.addLayer({
      id: LYR_FENCE_FILL,
      type: 'fill',
      source: SRC_FENCES,
      paint: {
        'fill-color': fenceColorExpr() as ExpressionSpecification,
        'fill-opacity': [
          'case', ['boolean', ['feature-state', 'selected'], false], 0.26, 0.09,
        ],
      },
    }, beforeId)
  }

  if (!map.getLayer(LYR_FENCE_LINE)) {
    map.addLayer({
      id: LYR_FENCE_LINE,
      type: 'line',
      source: SRC_FENCES,
      paint: {
        'line-color': fenceColorExpr() as ExpressionSpecification,
        'line-opacity': 0.7,
        'line-width': ['case', ['boolean', ['feature-state', 'selected'], false], 2.5, 1],
      },
    }, beforeId)
  }

  // The badge. Overlap is ALLOWED on purpose: two fences 200 m apart at the Dhar gate are
  // genuinely two things, and hiding one of them because its neighbour got there first
  // makes the map lie about what is on the ground. Names below still collide-avoid.
  if (!map.getLayer(LYR_FENCE_BADGE)) {
    map.addLayer({
      id: LYR_FENCE_BADGE,
      type: 'symbol',
      source: SRC_FENCES,
      minzoom: 9,
      layout: {
        'icon-image': fenceIconExpr() as unknown as ExpressionSpecification,
        'icon-size': ['interpolate', ['linear'], ['zoom'], 9, 0.42, 12, 0.62, 16, 0.85],
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'icon-anchor': 'center',
      },
      paint: {
        // Interpolate on the OUTSIDE, case on the inside. A "zoom" expression is only
        // legal as the direct input of a top-level step/interpolate; nested inside a
        // case, MapLibre rejects the whole layer at validation and adds nothing -- which
        // is silent, because the failure arrives on the map's 'error' event rather than
        // as a throw. Same result either way: selected reads full, the rest fade in
        // 0.78 -> 0.95 across z9-z12.
        'icon-opacity': [
          'interpolate', ['linear'], ['zoom'],
          9, ['case', ['boolean', ['feature-state', 'selected'], false], 1, 0.78],
          12, ['case', ['boolean', ['feature-state', 'selected'], false], 1, 0.95],
        ],
      },
    })
  }

  // Names appear only once they can be read without overlapping. Below z11 the corridor
  // is 173 km on screen and 25 labels is noise.
  if (!map.getLayer(LYR_FENCE_LABEL)) {
    map.addLayer({
      id: LYR_FENCE_LABEL,
      type: 'symbol',
      source: SRC_FENCES,
      minzoom: 11,
      layout: {
        'text-field': ['get', 'name'],
        'text-font': ['Noto Sans Medium'],
        'text-size': ['interpolate', ['linear'], ['zoom'], 11, 9.5, 15, 12],
        'text-offset': [0, 1.5],
        'text-anchor': 'top',
        'text-max-width': 12,
        'text-allow-overlap': false,
      },
      paint: {
        // Ink on a white halo: a label has to survive a dark satellite tile and a pale
        // paper one without a second style, and a halo does that where a colour cannot.
        'text-color': '#0F172A',
        'text-halo-color': 'rgba(255,255,255,0.92)',
        'text-halo-width': 1.6,
      },
    })
  }
}

export function updateReference(map: MlMap, geo: GeoPayload): void {
  const fences = map.getSource(SRC_FENCES)
  if (fences instanceof GeoJSONSource) fences.setData(geo.geofences)
  const routes = map.getSource(SRC_ROUTES)
  if (routes instanceof GeoJSONSource) routes.setData(geo.routes)
}
